import React, { useState, useEffect } from 'react';
import ProfileList from './ProfileList';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Redirect, Link } from 'react-router-dom';
import { signUpStatus, profileSortingOptions } from '../../constants/constants';
import { InfiniteScroll } from '../general/InfiniteScroll';
import { profileFields } from '../../constants/constants';
import useQueryServices from '../../hooks/useQueryServices';
import DropdownMenu from '../general/DropdownMenu';
import { getActiveCountriesNameArray, getActiveCountryStatesArray } from '../../utils/CountryUtil';
import { makeStyles, Grid } from '@material-ui/core';
import AlphabetPicker from '../general/AlphabetPicker';
import ProfilesSortDropdownMenu from './ProfilesSortDropdownMenu';
import BatchInviteModal from './BatchInviteModal';
import StyledButton from '../general/StyledButton';

const useStyles = makeStyles((theme) => ({
  filtersSection: {
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
      borderColor: '#009688',
    },
  },
}));

const Profiles = ({ auth }) => {
  const classes = useStyles();
  // Dropdown menu variables
  const DEFAULT_PLACEHOLDER = 'ALL';

  const SIGNUP_FILTER_NAME = 'Sign Up Status:';

  const COUNTRY_FILTER_NAME = 'Country:';
  const ACTIVE_COUNTRIES = getActiveCountriesNameArray();
  const COUNTRY_FIRST_PROPERTY_NAME = 'countryName';
  const COUNTRY_SECOND_PROPERTY_NAME = 'countryCode';

  const STATE_FILTER_NAME = 'Province/State:';
  const STATE_FIRST_PROPERTY_NAME = 'name';
  const STATE_SECOND_PROPERTY_NAME = 'shortCode';

  const [selectedSignUpStatus, setSelectedSignUpStatus] = useState(DEFAULT_PLACEHOLDER);
  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_PLACEHOLDER);
  const [selectedState, setSelectedState] = useState(DEFAULT_PLACEHOLDER);
  const [statesList, setStatesList] = useState([]);

  // Alphabet Picker variable
  const [selectedLetter, setSelectedLetter] = useState('');

  // Sort by variables
  const SORT_BY_LABEL = 'Sort By:';
  const DEFAULT_SORT_BY = profileFields.EMAIL;

  const [selectedSortByOption, setSelectedSortByOption] = useState(DEFAULT_SORT_BY);
  const [searchBarLabel, setSearchBarLabel] = useState(DEFAULT_SORT_BY);

  // Search filter variable
  const [searchTerm, setSearchTerm] = useState('');

  const { documents: profiles, loadingMore, noMoreResults, removeAllFilters, getNextDocuments, loadAllDocuments, loadFilteredDocuments, loadSearchDocument, addToFilters, removeFilter, addRangeFilter, setOrderBy, loadDocumentsWithNewSort } = useQueryServices('profiles', DEFAULT_SORT_BY);
  // Batch Invite Modal variable
  const [openBatchInviteModal, setOpenBatchInviteModal] = useState(false);

  useEffect(() => {
    loadAllDocuments(true);
  }, [loadAllDocuments]);

  const handleAlphabetPickerFilter = (optionSelected) => {
    setSearchTerm('');
    setSelectedLetter(optionSelected);

    // remove preivous filters
    removeAllAlphabetFilters();

    // if filterOption is # then add filter to find profile starting with a number
    if (optionSelected === '#') {
      addRangeFilter(selectedSortByOption, '>=', '0', '<', ':');
    }
    // check if sort by is first or last name
    else if (selectedSortByOption === profileSortingOptions.FIRST_NAME || selectedSortByOption === profileSortingOptions.LAST_NAME) {
      let upperCaseLetter = optionSelected.toUpperCase();

      let upperFirstLetterCharCode = upperCaseLetter.charCodeAt(0);
      let upperEndLetter = String.fromCharCode(upperFirstLetterCharCode + 1);

      addRangeFilter(selectedSortByOption, '>=', upperCaseLetter, '<', upperEndLetter);
    } else {
      // else add filter to find profile according to selected letter
      let firstLetterCharCode = optionSelected.charCodeAt(0);
      let endLetter = String.fromCharCode(firstLetterCharCode + 1);

      addRangeFilter(selectedSortByOption, '>=', optionSelected, '<', endLetter);
    }

    loadFilteredDocuments(true);
  };

  const removeAllAlphabetFilters = () => {
    removeFilter(profileSortingOptions.EMAIL);
    removeFilter(profileSortingOptions.FIRST_NAME);
    removeFilter(profileSortingOptions.LAST_NAME);
  };

  const handleStatusFilterChange = (status) => {
    setSearchTerm('');

    setSelectedSignUpStatus(status);

    if (status === DEFAULT_PLACEHOLDER) {
      removeFilter(profileFields.SIGN_UP_STATUS);
    } else {
      addToFilters(profileFields.SIGN_UP_STATUS, '==', status);
    }

    loadFilteredDocuments(true);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSelectedSortByOption(DEFAULT_SORT_BY);
    setSearchBarLabel(DEFAULT_SORT_BY);

    setSelectedLetter('');
    setSelectedSignUpStatus(DEFAULT_PLACEHOLDER);
    setSelectedCountry(DEFAULT_PLACEHOLDER);
    setSelectedState(DEFAULT_PLACEHOLDER);
    setStatesList([]);

    removeAllFilters();

    setOrderBy(DEFAULT_SORT_BY);

    loadAllDocuments(true);
  };

  const search = () => {
    removeAllFilters();

    // reset other filter dropdown menu
    setSelectedSignUpStatus(DEFAULT_PLACEHOLDER);
    setSelectedCountry(DEFAULT_PLACEHOLDER);
    setSelectedState(DEFAULT_PLACEHOLDER);
    setSelectedLetter('');
    setStatesList([]);

    loadSearchDocument(selectedSortByOption, '==', searchTerm);
    removeFilter(selectedSortByOption);
  };

  const loadNextResults = () => {
    getNextDocuments();
  };

  const handleCountryFilterChange = (countryCode) => {
    setSearchTerm('');

    setSelectedCountry(countryCode);

    if (countryCode === DEFAULT_PLACEHOLDER) {
      setSelectedCountry(DEFAULT_PLACEHOLDER);
      setSelectedState(DEFAULT_PLACEHOLDER);
      setStatesList([]);

      removeFilter(profileFields.COUNTRY_CODE);
      removeFilter(profileFields.STATE_CODE);
    } else {
      removeFilter(profileFields.STATE_CODE);
      setStatesList(getActiveCountryStatesArray(countryCode));
      setSelectedState(DEFAULT_PLACEHOLDER);

      addToFilters(profileFields.COUNTRY_CODE, '==', countryCode);
    }

    loadFilteredDocuments(true);
  };

  const handleStateFilterChange = (stateCode) => {
    setSelectedState(stateCode);

    if (stateCode === DEFAULT_PLACEHOLDER) {
      removeFilter(profileFields.STATE_CODE);
    } else {
      addToFilters(profileFields.STATE_CODE, '==', stateCode);
    }

    loadFilteredDocuments(true);
  };

  const handleBatchInviteButton = () => {
    setOpenBatchInviteModal(true);
  };

  const handleCloseBatchInviteModal = () => {
    setOpenBatchInviteModal(false);
  };

  const setCorrectSearchBarLabel = (sort) => {
    switch (sort) {
      case profileSortingOptions.EMAIL:
        setSearchBarLabel('email');
        break;
      case profileSortingOptions.FIRST_NAME:
        setSearchBarLabel('first name');
        break;
      case profileSortingOptions.LAST_NAME:
        setSearchBarLabel('last name');
        break;
      default:
        setSearchBarLabel('email');
        console.error('Invalid sort by options');
    }
  };

  const handleSortByChange = (sortBy) => {
    setSearchTerm('');
    setCorrectSearchBarLabel(sortBy);
    setSelectedSortByOption(sortBy);
    removeAllFilters();

    // re-add filters to fix sort by bug
    if (selectedLetter) {
      readdAlphabetPickerFilter(sortBy);
    }

    if (selectedSignUpStatus && selectedSignUpStatus !== DEFAULT_PLACEHOLDER) {
      addToFilters(profileFields.SIGN_UP_STATUS, '==', selectedSignUpStatus);
    }

    if (selectedCountry && selectedCountry !== DEFAULT_PLACEHOLDER) {
      addToFilters(profileFields.COUNTRY_CODE, '==', selectedCountry);
    }

    if (selectedState && selectedState !== DEFAULT_PLACEHOLDER) {
      addToFilters(profileFields.STATE_CODE, '==', selectedState);
    }

    loadDocumentsWithNewSort(true, sortBy);
  };

  const readdAlphabetPickerFilter = (sortBy) => {
    if (selectedLetter === '#') {
      addRangeFilter(sortBy, '>=', '0', '<', ':');
    }
    // check if sort by is first or last name
    else if (sortBy === profileSortingOptions.FIRST_NAME || sortBy === profileSortingOptions.LAST_NAME) {
      let upperCaseLetter = selectedLetter.toUpperCase();

      let upperFirstLetterCharCode = upperCaseLetter.charCodeAt(0);
      let upperEndLetter = String.fromCharCode(upperFirstLetterCharCode + 1);

      addRangeFilter(sortBy, '>=', upperCaseLetter, '<', upperEndLetter);
    } else {
      // else add filter to find profile according to selected letter
      let firstLetterCharCode = selectedLetter.charCodeAt(0);
      let endLetter = String.fromCharCode(firstLetterCharCode + 1);

      addRangeFilter(sortBy, '>=', selectedLetter, '<', endLetter);
    }
  };

  const renderSearchInput = () => (
    <div className="row">
      <div className="input-field col l7 m12 s12">
        <input id="searchInput" type="text" className="validate" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <label htmlFor="searchInput">{`Search by ${searchBarLabel}`}</label>
      </div>
      <div className="input-field col l5 m12 s12 search-button-field">
        <StyledButton text={'Search'} onClickHandler={search} />
        <StyledButton text={'Clear'} onClickHandler={clearSearch} />
      </div>
    </div>
  );

  const renderLoadingMore = () => {
    return loadingMore && <h5 style={{ textAlign: 'center' }}>Loading more...</h5>;
  };

  const renderButtonToLoadMore = () => {
    return (
      !noMoreResults &&
      loadingMore && (
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <button className="waves-effect waves-light btn-small" onClick={loadNextResults}>
            Load more
          </button>
        </div>
      )
    );
  };

  const renderFilterInputs = () => {
    return (
      <Grid container className={classes.filtersSection} spacing={1}>
        <Grid item xs={12} md={6}>
          {renderSearchInput()}
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownMenu name={COUNTRY_FILTER_NAME} items={ACTIVE_COUNTRIES} itemName={COUNTRY_FIRST_PROPERTY_NAME} itemValue={COUNTRY_SECOND_PROPERTY_NAME} placeHolder={DEFAULT_PLACEHOLDER} onChangeHandler={handleCountryFilterChange} isItemsAnArray={true} value={selectedCountry} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownMenu name={SIGNUP_FILTER_NAME} items={signUpStatus} itemName="" itemValue="" placeHolder={DEFAULT_PLACEHOLDER} onChangeHandler={handleStatusFilterChange} isItemsAnArray={false} value={selectedSignUpStatus} />
        </Grid>
        <Grid item xs={12} md={6}>
          <DropdownMenu name={STATE_FILTER_NAME} items={statesList} itemName={STATE_FIRST_PROPERTY_NAME} itemValue={STATE_SECOND_PROPERTY_NAME} placeHolder={DEFAULT_PLACEHOLDER} onChangeHandler={handleStateFilterChange} isItemsAnArray={true} value={selectedState} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ProfilesSortDropdownMenu name={SORT_BY_LABEL} onChangeHandler={handleSortByChange} value={selectedSortByOption} />
        </Grid>
      </Grid>
    );
  };

  if (!auth.uid) return <Redirect to="/signin" />;

  return (
    <div className="container">
      <div className="title-section">Profiles</div>
      {renderFilterInputs()}
      <div className="row batch-invite-button">
        <StyledButton text={'BATCH INVITE'} onClickHandler={handleBatchInviteButton} />
      </div>
      <BatchInviteModal openStatus={openBatchInviteModal} handleClose={handleCloseBatchInviteModal} />
      <InfiniteScroll hasMoreData={!noMoreResults} isLoading={!loadingMore} onBottomHit={loadNextResults} loadOnMount={true}>
        <div className="row">
          <div className="col m11 l11">
            {profiles.length ? (
              <ProfileList profiles={profiles} updateProfiles={clearSearch} />
            ) : (
              <div className="no-match-output">
                <span>No Matching Profile</span>
              </div>
            )}
          </div>
          <div className="col m1 l1 alphabetpicker">
            <AlphabetPicker onClickHandler={handleAlphabetPickerFilter} letterValue={selectedLetter} />
          </div>
        </div>
      </InfiniteScroll>
      {renderLoadingMore()}
      {renderButtonToLoadMore()}

      <Link to={window.location} className="btn-floating btn-large fixed-action-btn green right">
        <i className="material-icons">add</i>
      </Link>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.firebase.auth,
  };
};

export default compose(connect(mapStateToProps))(Profiles);
